import React, { useState } from 'react';
import { useTheme } from 'styled-components';

import { ALTURA_PLOT, AreaPlot, Grafico, MARGEM_TOPO, Rolagem } from './styles';
import { ALTURA_SVG, EixoY, Grade, PainelSelecao, RotuloX, criarEscalaY } from './Eixo';
import { passoRotulos, ticksAgradaveis } from './escala';
import Legenda from './Legenda';
import useLargura from './useLargura';
import { Formato, Serie } from '../../types/Dashboard';

const RECUO = 10; // espaço para o marcador do primeiro/último ponto

// Evolução no tempo (ex.: saldo projetado). O ponto mais próximo do mouse mostra os valores.
export default function Linha({ series, formato }: { series: Serie[]; formato: Formato }) {
  const theme = useTheme();
  const { ref, largura } = useLargura<HTMLDivElement>();
  const [sel, setSel] = useState<number | null>(null);

  const qtd = Math.max(...series.map(s => s.pontos.length));
  const valores = series.flatMap(s => s.pontos.map(p => p.valor));
  const ticks = ticksAgradaveis(Math.min(...valores), Math.max(...valores));
  const y = criarEscalaY(ticks);
  const passoX = qtd > 1 ? (largura - RECUO * 2) / (qtd - 1) : 0;
  const x = (i: number) => (qtd > 1 ? RECUO + i * passoX : largura / 2);
  const passo = passoRotulos(qtd, passoX || largura);

  function apontar(e: React.MouseEvent<SVGRectElement>) {
    if (qtd === 0) return;
    const posicao = e.clientX - e.currentTarget.getBoundingClientRect().left;
    const i = qtd > 1 ? Math.round((posicao - RECUO) / passoX) : 0;
    setSel(Math.min(qtd - 1, Math.max(0, i)));
  }

  return (
    <Grafico onMouseLeave={() => setSel(null)}>
      <PainelSelecao series={series} indice={sel} formato={formato} />
      <AreaPlot>
        <EixoY ticks={ticks} y={y} formato={formato} />
        <Rolagem ref={ref}>
          {largura > 0 && (
            <svg width={largura} height={ALTURA_SVG} role='img' aria-label={series.map(s => s.nome).join(', ')}>
              <Grade ticks={ticks} y={y} largura={largura} />
              {series.map((s, si) => (
                <path
                  key={s.nome}
                  d={s.pontos.map((p, i) => `${i === 0 ? 'M' : 'L'} ${x(i)} ${y(p.valor)}`).join(' ')}
                  stroke={theme.cores.serie[si]}
                  strokeWidth={2}
                  strokeLinejoin='round'
                  strokeLinecap='round'
                  fill='none'
                />
              ))}
              {qtd === 1 && series.map((s, si) => (
                <circle key={s.nome} cx={x(0)} cy={y(s.pontos[0].valor)} r={4} fill={theme.cores.serie[si]} />
              ))}
              {series[0]?.pontos.map((p, i) => (i % passo === 0 ? <RotuloX key={i} x={x(i)} texto={p.rotulo} largura={largura} /> : null))}
              {sel !== null && (
                <>
                  <line
                    x1={x(sel)}
                    x2={x(sel)}
                    y1={MARGEM_TOPO}
                    y2={MARGEM_TOPO + ALTURA_PLOT}
                    stroke={theme.cores.inkMuted}
                    strokeWidth={1}
                  />
                  {series.map((s, si) => s.pontos[sel] && (
                    <circle
                      key={s.nome}
                      cx={x(sel)}
                      cy={y(s.pontos[sel].valor)}
                      r={5}
                      fill={theme.cores.serie[si]}
                      stroke={theme.cores.card}
                      strokeWidth={2}
                    />
                  ))}
                </>
              )}
              {/* área de hover/toque: o gráfico inteiro */}
              <rect
                x={0}
                y={0}
                width={largura}
                height={ALTURA_SVG}
                fill='transparent'
                onMouseMove={apontar}
                onClick={apontar}
              />
            </svg>
          )}
        </Rolagem>
      </AreaPlot>
      <Legenda nomes={series.map(s => s.nome)} />
    </Grafico>
  );
}
